import React from 'react';
import { styled, css, Container, Flex, Box, Row, Column, Icon } from '@goodrx/matisse';
import { space } from 'styled-system';

import { SocialMediaPlatforms } from 'app/config/constants';
import { mediaMaxWidth } from 'app/lib/utils';
import { CoreLink, CoreLinkProps } from 'app/components/core/CoreLink/CoreLink';
import { CdnImage } from 'app/components/CdnImage';
import { Service } from 'app/types/Service';
import { tenancy } from 'app/lib/tenancy';

const Footer = styled.footer`
  background: ${(p) => p.theme.colors.greyLightest};
  padding: 32px 0;
  margin-top: 32px;
`;

const Logo = styled.img`
  width: 150px;
  margin-bottom: 0.5rem;
`;

const SectionRow = styled(Flex)`
  flex-wrap: wrap;
  justify-content: space-between;
`;

const SectionColumn = styled(Box)`
  padding: 0;

  ${mediaMaxWidth({
    sm: css`
      margin-top: 24px;
      width: 50%;
      padding: 0 12px 0 0;
    `,
  })};
`;

export const SectionTitle = styled.span`
  font-weight: 500;
  margin: 0 0 4px;
`;

const SectionList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 12px 0;
`;

const SectionListItem = styled.li`
  max-width: 250px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 12px 0;

  a {
    color: ${(p) => p.theme.colors.greyDarkest};
    text-decoration: none;
    transition: color 125ms;

    &:hover {
      color: black;
      ${Icon} {
        color: black;
      }
    }
  }
`;

const Line = styled.div`
  margin: 24px 0;
  background: ${(p) => p.theme.colors.grey};
  height: 1px;
`;

const BaseLink = styled.div`
  cursor: pointer;
  color: ${(p) => p.theme.colors.greyDarkest};
  text-decoration: none;
  transition: color 125ms;

  &:hover {
    color: black;
  }
`;

const FooterLink = ({ children, ...props }: CoreLinkProps) => {
  return (
    <CoreLink {...props}>
      <BaseLink>{children}</BaseLink>
    </CoreLink>
  );
};

const LegalLinks = styled(Flex)`
  justify-content: flex-end;

  > a {
    margin-right: ${(p) => p.theme.space[3]}px;
  }

  ${mediaMaxWidth({
    sm: css`
      margin-top: ${(p) => p.theme.space[4]}px;
    `,
    md: css`
      display: grid;
      grid-template-columns: 50% 50%;
      row-gap: ${(p) => p.theme.space[3]}px;
    `,
  })};
`;

const ExternalLink = styled.a.attrs(({ platform }: { platform: string }) => ({
  'aria-label': `Find us on ${platform}`,
  target: '_blank',
  rel: 'noopener noreferrer',
}))`
  color: ${(p) => p.theme.colors.greyDarkest};

  ${Icon} {
    transition: color 125ms;
  }

  &:hover {
    ${Icon} {
      color: black;
    }
  }

  ${space};
` as any;

interface MarketingFooterProps {
  services?: Service[];
}

export const MarketingFooter: React.FC<MarketingFooterProps> = ({ services }) => {
  return (
    <Footer>
      <Container>
        <Row gutter={1}>
          <Column xs={12} sm={3}>
            <CoreLink href="/">
              <Logo
                className="logo"
                src="//cdn.heydoctor.com/images/logos/heydoctor-wordmark-dark.svg"
                alt="HeyDoctor Logo"
              />
            </CoreLink>
            {!tenancy.isGoodRx() && (
              <Box>
                <CoreLink
                  absolute
                  href="http://legitscript.com/pharmacy/heydoctor.com"
                  title="Verify LegitScript Approval"
                  style={{ display: 'inline-block', height: 64 }}>
                  <CdnImage
                    src="//static.legitscript.com/seals/4048421.png"
                    alt="LegitScript approved"
                    width="90"
                    height="64"
                    border="0"
                  />
                </CoreLink>
              </Box>
            )}
          </Column>

          <Column xs={12} sm={9}>
            <SectionRow>
              {services && (
                <SectionColumn>
                  <SectionTitle>Services</SectionTitle>
                  <SectionList>
                    {services.slice(0, 4).map((service) => (
                      <SectionListItem key={service.slug}>
                        <CoreLink as={`/services/${service.slug}`} href="/services/[slug]">
                          {service.display_name || service.name}
                        </CoreLink>
                      </SectionListItem>
                    ))}
                  </SectionList>
                </SectionColumn>
              )}

              <SectionColumn>
                <SectionTitle>Company</SectionTitle>
                <SectionList>
                  <SectionListItem>
                    <CoreLink href="/about">About</CoreLink>
                  </SectionListItem>

                  <SectionListItem>
                    <a href="https://help.heydoctor.com" target="_blank" rel="noopener noreferrer">
                      Help
                    </a>
                  </SectionListItem>
                  <SectionListItem>
                    <a href="https://angel.co/heydoctor/jobs" target="_blank" rel="noopener noreferrer">
                      Careers
                    </a>
                  </SectionListItem>
                </SectionList>
              </SectionColumn>

              <SectionColumn>
                <SectionTitle>Resources</SectionTitle>
                <SectionList>
                  <SectionListItem>
                    <CoreLink href="/learn">Learn</CoreLink>
                  </SectionListItem>
                  <SectionListItem>
                    <FooterLink href="/learn/topics/[topic]" as="/learn/topics/general">
                      Methodology
                    </FooterLink>
                  </SectionListItem>

                  <SectionListItem>
                    <FooterLink href="#" absolute>
                      <Box
                        onClick={() => {
                          if (window.Osano) {
                            window.Osano.cm.emit('osano-cm-dom-info-dialog-open');
                          }
                        }}>
                        Cookie Preferences
                      </Box>
                    </FooterLink>
                  </SectionListItem>
                </SectionList>
              </SectionColumn>

              <SectionColumn>
                <SectionTitle>Follow Us</SectionTitle>
                <SectionList>
                  {SocialMediaPlatforms.map((platform, i) => (
                    <li key={platform.title} style={{ display: 'inline-block' }}>
                      <ExternalLink key={platform.icon} href={platform.link} platform={platform.title}>
                        <Icon
                          name={platform.icon}
                          color="greyDarker"
                          size={20}
                          style={{
                            marginLeft: i > 0 ? 12 : 0,
                          }}
                        />
                      </ExternalLink>
                    </li>
                  ))}
                </SectionList>
              </SectionColumn>
            </SectionRow>
          </Column>
        </Row>
        <Line />

        <Row>
          <Column xs={12} sm={6}>
            <FooterLink href="/">&copy; HeyDoctor, LLC {new Date().getFullYear()}</FooterLink>
          </Column>

          <Column xs={12} sm={6}>
            <LegalLinks>
              <FooterLink
                absolute
                target="_blank"
                rel="noopener noreferrer"
                href="http://help.heydoctor.com/en/articles/3575049-california-consumer-privacy-act-privacy-notice">
                Collection Notice
              </FooterLink>
              <FooterLink href="/legals/[code]" as="/legals/terms-and-conditions">
                Terms of Use
              </FooterLink>
              <FooterLink href="/legals/[code]" as="/legals/privacy-policy">
                Privacy Policy
              </FooterLink>
              <FooterLink href="/legals/[code]" as="/legals/disclaimer">
                Disclaimer
              </FooterLink>
              <FooterLink
                absolute
                target="_blank"
                rel="noopener noreferrer"
                href="https://support.goodrx.com/hc/en-us/articles/360032961692-Accessibility-on-GoodRx">
                Accessibility
              </FooterLink>
            </LegalLinks>
          </Column>
        </Row>
      </Container>
    </Footer>
  );
};
